import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  IconButton,
  Divider,
} from "@mui/material";
import { Delete, Edit, Save, Cancel } from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import { useCommentService } from "./commentService";
import { IComment } from "./types";

interface CommentsProps {
  referenceId: string;
  referenceType?: string;
}

export const Comments: React.FC<CommentsProps> = ({
  referenceId,
  referenceType = "Site",
}) => {
  const commentService = useCommentService();
  const [comments, setComments] = useState<IComment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadComments();
  }, [referenceId]);

  const loadComments = async () => {
    setLoading(true);
    try {
      const data = await commentService.getComments(referenceId, referenceType);
      setComments(data);
    } catch (err) {
      console.error("Error loading comments:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    const comment: Partial<IComment> = {
      referenceId: referenceId,
      referenceType: referenceType,
      commentText: newComment,
    };

    try {
      await commentService.postComment(comment);
      setNewComment("");
      loadComments();
    } catch (err) {
      console.error("Error adding comment:", err);
    }
  };

  const handleEditComment = (comment: IComment) => {
    setEditingId(comment.id);
    setEditText(comment.commentText);
  };

  const handleSaveEdit = async (commentId: string) => {
    const comment = comments.find((c) => c.id === commentId);
    if (!comment) return;

    const updatedComment: IComment = {
      ...comment,
      commentText: editText,
    };

    try {
      await commentService.putComment(commentId, updatedComment);
      setEditingId(null);
      setEditText("");
      loadComments();
    } catch (err) {
      console.error("Error updating comment:", err);
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm("Er du sikker på at du vil slette denne kommentaren?"))
      return;

    try {
      await commentService.deleteComment(commentId);
      loadComments();
    } catch (err) {
      console.error("Error deleting comment:", err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("nb-NO", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Kommentarer
      </Typography>

      {/* New Comment Input */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            label="Ny kommentar (støtter Markdown)"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            placeholder="Skriv din kommentar her... Du kan bruke Markdown formatering."
          />
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddComment}
              disabled={!newComment.trim()}
            >
              Legg til kommentar
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Comments List */}
      {loading && <Typography>Laster kommentarer...</Typography>}

      {!loading && comments.length === 0 && (
        <Typography color="textSecondary">Ingen kommentarer ennå</Typography>
      )}

      {!loading &&
        comments.map((comment) => (
          <Card key={comment.id} sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box flexGrow={1}>
                  <Typography variant="subtitle2" color="primary">
                    {comment.authorName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {formatDate(comment.createdOn)}
                  </Typography>
                </Box>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditComment(comment)}
                    title="Rediger"
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteComment(comment.id)}
                    title="Slett"
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />

              {editingId === comment.id ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                  />
                  <Box mt={1} display="flex" gap={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      startIcon={<Save />}
                      onClick={() => handleSaveEdit(comment.id)}
                    >
                      Lagre
                    </Button>
                    <Button
                      size="small"
                      startIcon={<Cancel />}
                      onClick={handleCancelEdit}
                    >
                      Avbryt
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ mt: 1 }}>
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => <Typography variant="body2" paragraph {...props} />,
                      h1: ({ node, ...props }) => <Typography variant="h4" gutterBottom {...props} />,
                      h2: ({ node, ...props }) => <Typography variant="h5" gutterBottom {...props} />,
                      h3: ({ node, ...props }) => <Typography variant="h6" gutterBottom {...props} />,
                      code: ({ node, inline, ...props }: any) => (
                        inline ?
                          <code style={{ backgroundColor: '#f5f5f5', padding: '2px 4px', borderRadius: '3px' }} {...props} /> :
                          <code style={{ display: 'block', backgroundColor: '#f5f5f5', padding: '8px', borderRadius: '3px', whiteSpace: 'pre-wrap' }} {...props} />
                      ),
                    }}
                  >
                    {comment.commentText}
                  </ReactMarkdown>
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
    </Box>
  );
};
